import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import {
    type IODDConfig,
    ioLinkMasterConnectionConfigSchema,
} from '@redviking/argonaut-util/types/connections/io-link-master.connection-config.zod';
import { SelectAllConnectionsDocument, type SelectAllConnectionsQuery } from 'types/db';
import { gqlClient } from 'src/util/gql-client';
import { Notify } from 'src/notifications';
import { CausalError } from '@redviking/causal-error';
import { fromZodError } from 'zod-validation-error';

export type ISDUVariables = NodeCfg<'isduRead'>['controlValues']['isduVariables'];

function resolveDataKind (variable: IODDConfig['variables'][string], dataTypes: IODDConfig['dataTypes']): ISDUVariables[string]['dataKind'] | null {
    const dataKind = variable.variableData.kind === 'dataRefLookup'
        ? dataTypes[variable.variableData.dataTypeRef]?.dataKind
        : variable.variableData.dataType.dataKind;
    if (!dataKind) {
        return null;
    }
    // object (complex) data types are exposed as strings
    return dataKind === 'object' ? 'string' : dataKind;
}

/**
 * Fetches the IODD config of the device on the given channel of an io link master connection and returns every ISDU variable
 * that supports the requested access, so they can be offered as options on the ISDU read/write nodes.
 *
 * Returns an empty object while the node is not fully configured yet (no connection or channel selected).
 * @throws a `CausalError` describing why the variables could not be loaded
 */
async function fetchISDUVariableOptions (payload: {
    connectionId: string;
    channelNumber: number;
    access: 'r' | 'w';
}): Promise<ISDUVariables> {
    if (!payload.connectionId || payload.channelNumber < 0) {
        return {};
    }
    let result: SelectAllConnectionsQuery;
    try {
        result = await gqlClient.request({
            document: SelectAllConnectionsDocument,
            variables: { filter: { connection_id: { _eq: payload.connectionId } } },
        });
    } catch (err) {
        throw new CausalError('Failed to fetch the IO Link master connection config', err as Error);
    }
    const connectionConfig = result.argo_connection_config[0];
    if (!connectionConfig) {
        throw new CausalError('The selected connection does not have a released config. Release the connection version that contains the IODD to see its ISDU variables');
    }
    // the `config` column only stores the connection-kind-specific details; the discriminant `kind` lives on the
    // connection itself and needs to be recombined before parsing
    const parsed = ioLinkMasterConnectionConfigSchema.safeParse({
        kind: connectionConfig.connection.kind,
        details: connectionConfig.config,
    });
    if (!parsed.success) {
        throw new CausalError('The IO Link master connection config is invalid', fromZodError(parsed.error));
    }
    const channelConfig = Object.entries(parsed.data.details.channels)
        .find(([ channelNumber ]) => Number(channelNumber) === payload.channelNumber)?.[1];
    if (!channelConfig) {
        throw new CausalError(`The selected connection does not have a channel ${payload.channelNumber}`);
    }
    if (channelConfig.kind !== 'iodd' || !channelConfig.config) {
            return {};
        }
    const { variables, dataTypes } = channelConfig.config;
    const isduVariables: ISDUVariables = {};
    for (const [ variableId, variable ] of Object.entries(variables)) {
        if (variable.access === payload.access || variable.access === 'rw') {
            const dataKind = resolveDataKind(variable, dataTypes);
            if (!dataKind) {
                throw new CausalError(`Could not resolve the data type of ISDU variable "${variableId}"`);
            }
            isduVariables[variableId] = {
                dataKind,
                tracked: false,
                name: variable.variableData.displayName,
            };
        }
    }
    return isduVariables;
}

/** same as `fetchISDUVariableOptions`, but reports failures to the user and resolves to no options instead of throwing */
export async function getISDUVariableOptions (payload: Parameters<typeof fetchISDUVariableOptions>[0]): Promise<ISDUVariables> {
    try {
        return await fetchISDUVariableOptions(payload);
    } catch (err) {
        Notify.error(CausalError.from({
            message: 'Unable to load ISDU variables',
            cause: err,
        }));
        return {};
    }
}
